import { useMemo, useEffect, useState } from 'react'
import React from 'react'
import PropTypes from 'prop-types'
import {
  Typography,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  TextField as MuiTextField,
} from '@mui/material'
import shadows from '@mui/material/styles/shadows'
import Stack from '@mui/material/Stack'
import { Field, Form, Formik } from 'formik'
import { TextField } from 'formik-mui'
import * as Yup from 'yup'
import { useTranslation } from 'react-i18next'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { format } from 'date-fns'
import { bffService } from '../../../../../../services/utils/axios'
import {
  RadioContainer,
  HiddenRadio,
  RadioLabel,
  RadioCustom,
  CheckboxControlContainer,
  CheckboxFieldContainer,
  StyledCheckboxCard,
} from '../../../styles'
import ErrorFeedback from '../../../../../../components/DataTable/Feedback'
import { useDispatch } from 'react-redux'
import { setBillingAddressSame } from '../../../../../../redux/slices/addressSlice'
import { getDateOnly } from '../../../components/formatDate'

/**
 * EditUserDetails Component
 *
 * A form component for editing user personal or company information as the first step
 * in the user details edit workflow. Dynamically changes fields based on entity type.
 *
 * @component
 * @param {Object} props - Component properties
 * @param {Object} props.payload - Initial user data to populate the form
 * @param {string} [props.payload.salutation] - User salutation (Mr/Mrs/Firma)
 * @param {string} [props.payload.fullName] - User's full name
 * @param {Object} [props.payload.address] - User's address information
 * @param {boolean} [props.payload.isBillingAddressSameAsAddress] - Whether billing address is same as main address
 * @param {Function} props.setCanNext - Function to enable/disable the next button in parent stepper
 * @param {Function} props.setPayload - Function to update the parent component's state with form data
 * @param {Function} props.confirmPayload - Function to be called after successful form submission
 * @param {number} props.submit - Trigger for form submission from parent
 * @param {Function} props.setSubmit - Function to reset the submission trigger
 * @returns {React.ReactElement} The rendered form component
 */
function EditUserDetails({
  payload,
  setCanNext,
  setPayload,
  confirmPayload,
  submit,
  setSubmit,
}) {
  const { t } = useTranslation()
  const [streets, setStreets] = useState([payload?.address?.street] || [])
  const [showError, setShowError] = useState(false)
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(
      setBillingAddressSame(payload?.isBillingAddressSameAsAddress || false)
    )
  }, [dispatch, payload?.isBillingAddressSameAsAddress])

  const closeFeedback = () => {
    setShowError(false)
    setStreets([null])
  }

  useEffect(() => {
    if (submit > 0) document.getElementById('sub').click()
    setSubmit(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submit])

  const handleSelectChange = (e, setFieldValue) => {
    setFieldValue('street', e.target.value)
  }

  const handlePostalCodeChange = async (e, setFieldValue) => {
    const newPostalCode = e.target.value
    setFieldValue('postalCode', newPostalCode)

    if (newPostalCode.length === 5) {
      try {
        const { data } = await bffService.get('/valid-addresses')
        if (data && Array.isArray(data)) {
          // Filter addresses by postal code
          const matchingAddresses = data.filter(
            (address) => address.postalCode === newPostalCode
          )

          if (matchingAddresses.length > 0) {
            // Extract unique streets and city from matching addresses
            const streets = [
              ...new Set(matchingAddresses.map((addr) => addr.street)),
            ]
            const city = matchingAddresses[0].city // Use first matching address for city

            setStreets(streets)
            setFieldValue('city', city)
          } else {
            setFieldValue('postalCode', '')
            setShowError(true)
          }
        } else {
          setFieldValue('postalCode', '')
          setShowError(true)
        }
      } catch (error) {
        setFieldValue('postalCode', '')
        setFieldValue('street', [null])
        setShowError(true)
      }
    }
  }
  // Helper function to convert API salutation to form salutation
  const convertApiSalutationToForm = (apiSalutation) => {
    switch (apiSalutation) {
      case 'MR':
        return 'Mr'
      case 'MRS':
        return 'Mrs'
      case 'COMPANY':
        return 'COMPANY'
      default:
        return ''
    }
  }

  // Helper function to convert form salutation to API salutation
  const convertFormSalutationToApi = (formSalutation) => {
    switch (formSalutation) {
      case 'Mr':
        return 'MR'
      case 'Mrs':
        return 'MRS'
      case 'COMPANY':
        return 'COMPANY'
      default:
        return ''
    }
  }

  const initialValues = {
    salutation: convertApiSalutationToForm(payload?.salutation) || '',
    firstName: payload?.firstName || '',
    lastName: payload?.lastName || '',
    companyName: payload?.companyName || '',
    emailAddress: payload?.email || payload?.emailAddress || '',
    phoneNumber: payload?.phone || payload?.phoneNumber || '',
    dateOfBirth: getDateOnly(payload?.dateOfBirth) || '',
    street: payload?.address?.street || [],
    houseNumber: payload?.address?.houseNumber || '',
    city: payload?.address?.city || '',
    postalCode: payload?.address?.postalCode || '',
    registeredCourt: payload?.registeredCourt || '',
    registrationNumber: payload?.registrationNumber || '',
    addressSame: payload?.isBillingAddressSameAsAddress || false,
  }

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        salutation: Yup.string()
          .required(t('LBLsalutationreq'))
          .label(t('LBLSalutation')),
        firstName: Yup.string().when('salutation', {
          is: (val) => val !== 'COMPANY',
          then: () => Yup.string().required(t('LBLFullNameReq')),
        }),
        lastName: Yup.string().when('salutation', {
          is: (val) => val !== 'COMPANY',
          then: () => Yup.string().required(t('LBLFullNameReq')),
        }),
        companyName: Yup.string().when('salutation', {
          is: (val) => val === 'COMPANY',
          then: () => Yup.string().required(t('LBLCompanyNameReq')),
        }),
        emailAddress: Yup.string()
          .email(t('wrongEmail'))
          .required(t('LBLEmailReq'))
          .label('E-Mail'),
        street: Yup.string().when('salutation', {
          is: (val) => val !== 'COMPANY',
          then: () =>
            Yup.string()
              .required(t('LBLStNameReq'))
              .typeError(t('LBLStreetError'))
              .label('LBLStName'),
        }),
        city: Yup.string().when('salutation', {
          is: (val) => val !== 'COMPANY',
          then: () => Yup.string().required(t('LBLLocReq')),
        }),
        houseNumber: Yup.string().required(t('LBLhouseNrReq')),
        postalCode: Yup.number().when('salutation', {
          is: (val) => val !== 'COMPANY',
          then: () =>
            Yup.number()
              .required(t('LBLPostalCodeError'))
              .label(t('PostalCode')),
        }),
        dateOfBirth: Yup.date(),
        phoneNumber: Yup.string().label(t('LBLHandy')),
        registeredCourt: Yup.string().when('salutation', {
          is: (val) => val === 'COMPANY',
          then: () => Yup.string().required(t('LBLRegCourtReq')),
        }),
        registrationNumber: Yup.string().when('salutation', {
          is: (val) => val === 'COMPANY',
          then: () => Yup.string().required(t('LBLRegNumReq')),
        }),
        addressSame: Yup.bool().label('LBLAddressSame'),
      }),
    [t]
  )
  /** @type {import("formik").FormikConfig<typeof initialValues>['onSubmit']} */
  const handleSubmit = async (values, actions) => {
    const dateOfBirth = values?.dateOfBirth
      ? new Date(values?.dateOfBirth)
      : undefined
    const payload = {
      salutation: convertFormSalutationToApi(values?.salutation),
      firstName: values?.firstName,
      lastName: values?.lastName,
      companyName: values?.companyName,
      registeredCourt: values?.registeredCourt || '',
      registrationNumber: values?.registrationNumber || '',
      email: values?.emailAddress,
      phone: values?.phoneNumber,
      dateOfBirth:
        dateOfBirth && !isNaN(dateOfBirth.getTime())
          ? format(dateOfBirth, 'yyyy-MM-dd')
          : undefined,
      address: {
        city: values?.city,
        street: values?.street,
        houseNumber: values?.houseNumber,
        postalCode: values?.postalCode,
      },
      addressSame: values?.addressSame,
    }

    setPayload(payload)
    confirmPayload()
    actions.setSubmitting(false)
    actions.setTouched({}, false)
    actions.setErrors({})
    actions.setFieldError({})
    actions.setFieldTouched({}, false, false)
  }

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnMount={true}
        onSubmit={handleSubmit}
        validateOnChange={true}
        validateOnBlur={true}
      >
        {({
          errors,
          touched,
          values,
          setFieldValue,
          setFieldError,
          setFieldTouched,
          isValid,
          handleChange,
          validateField,
        }) => (
          <Form noValidate>
            <>
              {setCanNext(touched && isValid)}
              <Stack my={5} direction="row" alignItems="center">
                <Field name="salutation">
                  {({ field }) => (
                    <>
                      <RadioContainer>
                        <HiddenRadio
                          {...field}
                          id="male"
                          value="Mr"
                          checked={field.value === 'Mr'}
                          name="salutation"
                          onChange={() => setFieldValue('salutation', 'Mr')}
                        />
                        <RadioLabel htmlFor="male">
                          <RadioCustom />
                          {t('MR')}
                        </RadioLabel>
                      </RadioContainer>

                      <RadioContainer>
                        <HiddenRadio
                          {...field}
                          id="female"
                          value="Mrs"
                          checked={field.value === 'Mrs'}
                          name="salutation"
                          onChange={() => setFieldValue('salutation', 'Mrs')}
                        />
                        <RadioLabel htmlFor="female">
                          <RadioCustom />
                          {t('MRS')}
                        </RadioLabel>
                      </RadioContainer>

                      <RadioContainer>
                        <HiddenRadio
                          {...field}
                          id="company"
                          value="COMPANY"
                          checked={field.value === 'COMPANY'}
                          name="salutation"
                          onChange={() =>
                            setFieldValue('salutation', 'COMPANY')
                          }
                        />
                        <RadioLabel htmlFor="company">
                          <RadioCustom />
                          {t('COMPANY')}
                        </RadioLabel>
                      </RadioContainer>
                    </>
                  )}
                </Field>
              </Stack>
              {/* If Selection is COMPANY */}
              {values.salutation === 'COMPANY' ? (
                <>
                  <Stack my={5} direction="row" alignItems="center" spacing={8}>
                    <Field
                      fullWidth
                      margin="none"
                      component={TextField}
                      id="companyName"
                      name="companyName"
                      label={t('LBLCompanyName')}
                      required
                      InputProps={{
                        title: 'Bitte füllen Sie dieses Feld aus',
                      }}
                      error={errors.companyName && touched.companyName}
                      helperText={touched.companyName && errors.companyName}
                    />

                    <Field
                      fullWidth
                      margin="none"
                      component={TextField}
                      id="registeredCourt"
                      name="registeredCourt"
                      label={t('LBLRegisterCourt')}
                      required
                      InputProps={{
                        title: 'Bitte füllen Sie dieses Feld aus',
                      }}
                      error={errors.registeredCourt && touched.registeredCourt}
                      helperText={
                        touched.registeredCourt && errors.registeredCourt
                      }
                    />
                  </Stack>
                  <Stack mb={5} direction="row" alignItems="center" spacing={8}>
                    <Field
                      fullWidth
                      margin="none"
                      component={TextField}
                      id="registrationNumber"
                      name="registrationNumber"
                      label={t('LBLRegisterNumber')}
                      InputProps={{
                        title: 'Bitte füllen Sie dieses Feld aus',
                      }}
                      required
                      error={
                        errors.registrationNumber && touched.registrationNumber
                      }
                      helperText={
                        touched.registrationNumber && errors.registrationNumber
                      }
                    />
                    <Field
                      fullWidth
                      margin="none"
                      component={TextField}
                      id="phoneNumber"
                      name="phoneNumber"
                      label={t('LBLHandy')}
                      InputProps={{
                        title: 'Bitte füllen Sie dieses Feld aus',
                      }}
                    />
                  </Stack>
                  <Stack mb={5} direction="row" alignItems="center" spacing={8}>
                    <Field
                      fullWidth
                      margin="none"
                      component={TextField}
                      id="emailAddress"
                      name="emailAddress"
                      label="E-Mail"
                      InputProps={{
                        title: 'Bitte füllen Sie dieses Feld aus',
                      }}
                      required
                    />
                  </Stack>
                </>
              ) : (
                <>
                  <Stack my={5} direction="row" alignItems="center" spacing={8}>
                    <Field
                      fullWidth
                      margin="none"
                      component={TextField}
                      id="firstName"
                      name="firstName"
                      label={t('LBLFirstName')}
                      required
                      InputProps={{
                        title: 'Bitte füllen Sie dieses Feld aus',
                      }}
                    />

                    <Field
                      fullWidth
                      margin="none"
                      component={TextField}
                      id="lastName"
                      name="lastName"
                      label={t('LBLLastName')}
                      required
                      InputProps={{
                        title: 'Bitte füllen Sie dieses Feld aus',
                      }}
                    />
                  </Stack>
                  <Stack mb={5} direction="row" spacing={8}>
                    <Field
                      fullWidth
                      required
                      margin="none"
                      component={TextField}
                      id="postalCode"
                      name="postalCode"
                      label={t('PostalCode')}
                      value={values.postalCode}
                      InputProps={{
                        title: 'Bitte füllen Sie dieses Feld aus',
                      }}
                      onChange={(e) => handlePostalCodeChange(e, setFieldValue)}
                    />
                    <MuiTextField
                      select
                      fullWidth
                      margin="none"
                      id="street"
                      name="street"
                      label={t('lblStName')}
                      required
                      InputProps={{
                        title: 'Bitte füllen Sie dieses Feld aus',
                      }}
                      error={Boolean(errors.street && touched.street)}
                      helperText={touched.street && errors.street}
                      defaultValue={payload?.address?.street}
                      value={values.street}
                      onChange={(e) => handleSelectChange(e, setFieldValue)}
                    >
                      {streets.map((street) => (
                        <MenuItem key={street} value={street}>
                          {street}
                        </MenuItem>
                      ))}
                    </MuiTextField>
                  </Stack>
                  <Stack mb={5} direction="row" spacing={8}>
                    <Field
                      fullWidth
                      margin="none"
                      component={TextField}
                      id="houseNumber"
                      name="houseNumber"
                      label={t('LBLHouseNr')}
                      InputProps={{
                        title: 'Bitte füllen Sie dieses Feld aus',
                      }}
                      required
                    />
                    <Field
                      required
                      fullWidth
                      margin="none"
                      component={TextField}
                      id="city"
                      name="city"
                      label={t('city')}
                      InputProps={{
                        title: 'Bitte füllen Sie dieses Feld aus',
                      }}
                      onChange={handleChange}
                      value={values.city}
                    />
                  </Stack>
                  <Stack mb={5} direction="row" spacing={8}>
                    <Field
                      fullWidth
                      margin="none"
                      component={TextField}
                      id="phoneNumber"
                      name="phoneNumber"
                      label={t('LBLHandy')}
                      InputProps={{
                        title: 'Bitte füllen Sie dieses Feld aus',
                      }}
                      sx={{ width: '500px' }}
                    />

                    <LocalizationProvider
                      dateAdapter={AdapterDayjs}
                      adapterLocale="de"
                    >
                      <DatePicker
                        label={t('LBLBirthDate')}
                        PaperProps={{
                          sx: {
                            boxShadow: shadows[3],
                          },
                        }}
                        value={values.dateOfBirth}
                        id={'dateOfBirth'}
                        onChange={(newValue) => {
                          setFieldValue(
                            'dateOfBirth',
                            newValue ? newValue.toDate() : ''
                          )
                        }}
                        renderInput={(params) => (
                          <Field
                            component={TextField}
                            {...params}
                            name="dateOfBirth"
                            margin="none"
                            autoComplete="off"
                            helperText={
                              touched.dateOfBirth && errors.dateOfBirth
                            }
                            error={errors.dateOfBirth && touched.dateOfBirth}
                            inputProps={{
                              ...params.inputProps,
                              placeholder: 'TT.MM.JJJJ',
                            }}
                            sx={{ width: '500px' }}
                          />
                        )}
                      />
                    </LocalizationProvider>
                  </Stack>
                  <Stack mb={5} direction="row" spacing={8}>
                    <Field
                      fullWidth
                      margin="none"
                      component={TextField}
                      id="emailAddress"
                      name="emailAddress"
                      label="E-Mail"
                      InputProps={{
                        title: 'Bitte füllen Sie dieses Feld aus',
                      }}
                      required
                    />
                  </Stack>
                </>
              )}

              <Stack my={2} direction="row" alignItems="center" spacing={1}>
                <FormControlLabel
                  control={
                    <CheckboxControlContainer>
                      <CheckboxFieldContainer>
                        <Field
                          component={Checkbox}
                          type="checkbox"
                          id="addressSame"
                          name="addressSame"
                          onChange={(event) => {
                            const isChecked = event.target.checked
                            setFieldValue('addressSame', isChecked)
                            dispatch(setBillingAddressSame(isChecked))
                          }}
                          checked={values.addressSame}
                        />
                      </CheckboxFieldContainer>
                      <StyledCheckboxCard variant="outline">
                        <Typography
                          sx={{ fontSize: 14 }}
                          color="text.secondary"
                          gutterBottom
                        >
                          {t('LBLBillingAddressMsg')}
                        </Typography>
                      </StyledCheckboxCard>
                    </CheckboxControlContainer>
                  }
                />
              </Stack>
              <Button sx={{ display: 'none' }} id="sub" type="submit"></Button>
            </>
          </Form>
        )}
      </Formik>
      <ErrorFeedback
        open={showError}
        title={t('LBLErrorTitle')}
        severity="warning"
        message={
          <Typography variant="subtitle1" sx={{ width: 400, mx: 10 }}>
            {t('LBLAuwAreaError')}
          </Typography>
        }
        handleClose={closeFeedback}
      />
    </>
  )
}
EditUserDetails.propTypes = {
  payload: PropTypes.objectOf(PropTypes.any),
  setPayload: PropTypes.func.isRequired,
  confirmPayload: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
}

export default EditUserDetails
